import { ScheduledCall } from '../types';
import { authService } from './auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.app.yib.io/api/v1';

// Helper function to get auth headers
const getAuthHeaders = (includeContentType: boolean = true): HeadersInit => {
  try {
    const token = authService.getToken();
    const headers: HeadersInit = {};
    
    if (includeContentType) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    return headers;
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return includeContentType ? { 'Content-Type': 'application/json' } : {};
  }
};

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  // Clone the response so we can read it multiple times if needed
  const clonedResponse = response.clone();
  
  // Check content type before parsing
  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  if (!response.ok) {
    let errorMessage = `Request failed: ${response.status} ${response.statusText}`;
    
    try {
      if (isJson) {
        const error = await clonedResponse.json();
        // Extract error message from various possible response formats
        errorMessage = error.message || error.error || errorMessage;
      } else {
        const text = await clonedResponse.text();
        errorMessage = text || errorMessage;
      }
    } catch (e) {
      // If parsing fails, use status text
      errorMessage = `Request failed: ${response.status} ${response.statusText}`;
    }
    
    throw new Error(errorMessage);
  }
  
  if (!isJson) {
    const text = await clonedResponse.text();
    throw new Error(`Expected JSON but got ${contentType || 'unknown content type'}. Response: ${text.substring(0, 100)}`);
  }
  
  return response.json();
};

// Campaign API Service

/** A row the importer could not use, and why. */
export interface SkippedRow {
  row: number;
  name: string;
  phone?: string;
  same_as_row?: number;
}

/** What an upload actually did. On the response to POST /campaigns/:id/lists. */
export interface ImportReport {
  summary: string;
  imported: number;
  total_rows: number;
  skipped_no_phone: SkippedRow[];
  skipped_unreachable: SkippedRow[];
  duplicates: SkippedRow[];
  unrecognised_columns: string[];
}

/** What launching would do. Nothing in here dials anyone. */
/** The contact list an upload created, with an account of what went into it. */
export interface UploadedContactList {
  id: number;
  name: string;
  campaign_id: number;
  /** Present on the response to the upload that created the list. */
  import?: ImportReport;
}

/** One call a campaign placed, and what became of it. */
export interface CampaignCall {
  scheduled_call_id: number;
  name: string | null;
  to_number: string;
  /**
   * `reached` / `voicemail` / `no_answer` come from Retell's record of the
   * call, not from this database — nothing here records them. `placed` means
   * the dial succeeded and the vendor record has not arrived yet, so it must
   * never be rendered as a conversation.
   */
  outcome: 'reached' | 'answered_brief' | 'voicemail' | 'no_answer'
         | 'in_progress' | 'failed' | 'placed' | 'not_yet';
  outcome_label: string;
  /** From Retell. null means not recorded, never zero seconds. */
  duration_seconds: number | null;
  /** Retell's read of how the patient sounded. */
  sentiment: string | null;
  /** Retell's summary of what was actually said on the call. */
  summary: string | null;
  has_transcript: boolean;
  called_at: string | null;
  interaction_id: number | null;
}

/**
 * What a campaign did. Classified server-side on purpose.
 *
 * This panel used to be derived in the browser from GET /campaigns/:id/calls,
 * with outcome buckets invented in the client. Classifying on the server means
 * the browser and the assistant cannot quietly disagree about how a campaign
 * went -- and it is where the knowledge lives about which of these columns
 * actually mean anything.
 */
export interface CampaignResults {
  campaign_id: number;
  total_scheduled: number;
  counts: Record<CampaignCall['outcome'], number>;
  reached: number;
  voicemail: number;
  placed: number;
  still_waiting: number;
  /**
   * True once Retell's record has been read for at least one call. While it is
   * false the report is dial-level only, and `reached` is 0 because nothing
   * knows yet — not because nobody answered.
   */
  outcome_recorded: boolean;
  calls_analysed: number;
  /** Lookups are capped per report; this says how many were skipped. */
  calls_not_analysed: number;
  calls: CampaignCall[];
  summary: string;
}

/** Whether this practice can run a campaign at all. */
export interface OutboundReadiness {
  ready: boolean;
  agents: { agent_id: number; name: string | null }[];
  agents_not_ready: { agent_id: number; name: string | null; why_not: string }[];
  from_numbers: { channel_id: string; name: string | null; number: string }[];
  missing: string[];
  summary: string;
}

export interface LaunchPreview {
  campaign_id: number;
  name: string;
  from_number: string;
  agent_id: number;
  /** People who would be called: duplicates and undialable numbers removed. */
  will_call: number;
  /** Rows on the lists, before that removal. */
  contacts_total: number;
  duplicates: { phone: string; names: string[] }[];
  unreachable: string[];
  /** Reasons the campaign cannot launch at all. */
  blocking: string[];
  /** Worth knowing, but not disqualifying. */
  warnings: string[];
  can_launch: boolean;
}

export const campaignApi = {
  // GET /campaigns
  getCampaigns: async () => {
    try {
      // Ensure we have a valid base URL
      const baseUrl = API_BASE_URL || '/api/v1';
      const url = `${baseUrl}/campaigns`;
      
      // Validate URL before making request
      if (!url || typeof url !== 'string') {
        throw new Error('Invalid URL: ' + url);
      }
      
      console.log('Fetching campaigns from URL:', url);
      console.log('Current window location:', window.location.href);
      
      const headers = getAuthHeaders(false);
      console.log('Request headers:', headers);
      console.log('About to call fetch...');
      
      const response = await fetch(url, {
        headers: headers,
      });
      
      console.log('Response received, status:', response.status);
      const data = await handleResponse(response);
      console.log('Response data:', data);
      // API returns { success: true, data: [...] }
      return Array.isArray(data?.data) ? data.data : [];
    } catch (error: any) {
      console.error('Error in getCampaigns:', error);
      console.error('Error name:', error?.name);
      console.error('Error message:', error?.message);
      console.error('Error stack:', error?.stack);
      throw error;
    }
  },

  // POST /campaigns
  createCampaign: async (campaignData: any) => {
    const response = await fetch(`${API_BASE_URL}/campaigns`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(campaignData),
    });
    const data = await handleResponse(response);
    return data.data; 
  },

  // PUT /campaigns/:id
  updateCampaign: async (campaignId: string, campaignData: any) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(campaignData),
    });
    const data = await handleResponse(response);
    return data.data;
  },

  // DELETE /campaigns/:id
  deleteCampaign: async (campaignId: string) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return data.results;
  },

  // POST /campaigns/:id/lists
  // POST /campaigns/:id/lists
  //
  // The response carries an `import` report. Show it: an upload that drops
  // rows still answers 200, and the count is the only thing that says so. A
  // file with no usable phone column comes back 400 naming the headers it
  // did find.
  uploadContactList: async (
    campaignId: string, file: File, listName: string,
  ): Promise<UploadedContactList> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('list_name', listName);

    const token = authService.getToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/lists`, {
      method: 'POST',
      headers: headers,
      body: formData,
    });
    const data = await handleResponse(response);
    return data.data;
  },

  // GET /campaigns/:id/lists
  getContactLists: async (campaignId: string) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/lists`, {
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return Array.isArray(data?.data) ? data.data : [];
  },

  // DELETE /campaigns/:id/lists/:listId
  deleteContactList: async (campaignId: string, listId: string) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/lists/${listId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return data.results;
  },

  // GET /campaigns/:id/preview
  // What launching would do: how many people, and what is wrong with it.
  // Read-only -- this dials nobody.
  previewCampaign: async (campaignId: string): Promise<LaunchPreview> => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/preview`, {
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return data.results || data.data;
  },

  // POST /campaigns/:id/launch
  // Places a real call to every contact. confirmCount must match the number of
  // people the server is about to call, and is re-checked there against the
  // live count -- so a launch confirmed against one list cannot dial a larger
  // one uploaded in between. A mismatch comes back 400 and nothing is dialled.
  launchCampaign: async (campaignId: string, confirmCount: number) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/launch`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ confirm_count: confirmCount }),
    });
    const data = await handleResponse(response);
    return data.data?.scheduled_calls || data.results?.scheduled_calls || data.results || [];
  },

  // GET /campaigns/:id/contacts
  getCampaignContacts: async (campaignId: string) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts`, {
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return Array.isArray(data?.data) ? data.data : [];
  },

  // PUT /campaigns/:id/contacts/:contactId
  updateContact: async (campaignId: string, contactId: string, contactData: any) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts/${contactId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(contactData),
    });
    const data = await handleResponse(response);
    return data.data;
  },

  // DELETE /campaigns/:id/contacts/:contactId
  deleteContact: async (campaignId: string, contactId: string) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/contacts/${contactId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return data.results;
  },

  // GET /campaigns/:id/calls - Get all calls for a campaign
  // GET /campaigns/:id/results
  //
  // Replaces the client-side derivation from /campaigns/:id/calls, which the
  // Python API does not serve -- see CampaignResults above. Classifying on the
  // server also means the browser cannot quietly disagree with the assistant
  // about how many people were reached.
  getCampaignResults: async (campaignId: string): Promise<CampaignResults> => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/results`, {
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return data.data;
  },

  // GET /campaigns/outbound-readiness — read-only; dials nobody.
  getOutboundReadiness: async (): Promise<OutboundReadiness> => {
    const response = await fetch(`${API_BASE_URL}/campaigns/outbound-readiness`, {
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return data.data;
  },

  getCampaignCalls: async (campaignId: string) => {
    const response = await fetch(`${API_BASE_URL}/campaigns/${campaignId}/calls`, {
      headers: getAuthHeaders(false),
    });
    const data = await handleResponse(response);
    return Array.isArray(data?.data) ? data.data : [];
  },
};